import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageSquareHeart, Loader2 } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK =
  "Sorry, I lost my train of thought. Try again — or tap Start Your Case and a human NLL Agent will take over.";

const getSessionId = () => {
  let id = localStorage.getItem("petty_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("petty_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi, I'm No Lawyer Chat. Someone owe you money? Tell me what happened.",
    },
  ]);
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { from: "user", text }, { from: "bot", text: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let failed = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5));
          if (payload.error) {
            failed = true;
          } else if (payload.delta) {
            const delta = payload.delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                from: "bot",
                text: copy[copy.length - 1].text + delta,
              };
              return copy;
            });
          }
        }
      }
      if (failed) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { from: "bot", text: FALLBACK };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { from: "bot", text: FALLBACK };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-container"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[65] w-[calc(100vw-2rem)] max-w-[360px] bg-white rounded-3xl shadow-2xl border border-[#E5E7EB] overflow-hidden flex flex-col"
          >
            <div className="bg-[#111827] px-5 py-4 flex items-center gap-3">
              <LogoMark size={34} className="bg-white rounded-lg p-0.5" />
              <div className="flex-1">
                <p className="font-display font-semibold text-[#FFFFFF] leading-tight">
                  No Lawyer Chat
                </p>
                <p className="text-xs text-[#93C5FD] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#93C5FD] inline-block" />
                  AI assistant — replies instantly
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                className="text-[#a1a1aa] hover:text-white p-1"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>

            <div
              ref={bodyRef}
              data-testid="chat-messages"
              className="h-72 overflow-y-auto px-4 py-4 space-y-3 bg-[#FFFFFF]"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-[#111827] text-[#FFFFFF] rounded-br-md"
                        : "bg-white text-[#111827] border border-[#E5E7EB] rounded-bl-md"
                    }`}
                  >
                    {m.text ||
                      (sending && i === messages.length - 1 ? (
                        <Loader2 size={16} className="animate-spin text-[#2563EB]" />
                      ) : (
                        m.text
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-3 py-3 bg-white border-t border-[#E5E7EB] flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Describe your dispute..."
                className="flex-1 bg-[#FFFFFF] border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={sending}
                className="w-10 h-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center justify-center transition-colors shrink-0 disabled:opacity-60"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-widget-trigger"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-4 sm:right-6 z-[65] w-14 h-14 rounded-full bg-[#2563EB] shadow-[0_14px_30px_-8px_rgba(37,99,235,0.6)] flex items-center justify-center text-white"
        aria-label="Open chat"
      >
        {open ? <X size={24} /> : <MessageSquareHeart size={26} />}
      </motion.button>
    </>
  );
};
